import React, { useState, useEffect } from 'react';
import {
  Card, Table, Button, DatePicker, Select, Space, Tooltip,
  Drawer, Descriptions, Divider, List, Tag, message, Upload, Alert,
  Row, Col, Form, Typography
} from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';
import { 
  SearchOutlined, FileWordOutlined, EyeOutlined, 
  FileTextOutlined, DownloadOutlined, QuestionCircleOutlined,
  FileOutlined, UploadOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import courseService from '../../services/courseService';
import { getSubjectsByCourse } from '../../services/subjectService';
import { generateExamService } from '../../services/generateExamService';
import { testBackend } from '../../utils/backendTest';
import type { 
  Course, 
  Subject, 
  ExamHistory, 
  ExamDetail,
  ExamHistoryQuery 
} from '../../../../shared/types/database';

const { Option } = Select;
const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

interface ExamHistoryPageState {
  loading: boolean;
  exams: ExamHistory[];
  courses: Course[];
  subjects: Subject[];
  selectedCourse: string | null;
  selectedSubject: string | null;
  dateRange: [dayjs.Dayjs, dayjs.Dayjs] | null;
  drawerVisible: boolean;
  selectedExam: ExamHistory | null;
  examDetail: ExamDetail | null;
  answerFile: File | null;
  uploadingAnswer: boolean;
  answerUploaded: boolean;
}

const ExamHistoryPage: React.FC = () => {
  const [state, setState] = useState<ExamHistoryPageState>({
    loading: false,
    exams: [],
    courses: [],
    subjects: [],
    selectedCourse: null,
    selectedSubject: null,
    dateRange: null,
    drawerVisible: false,
    selectedExam: null,
    examDetail: null,
    answerFile: null,
    uploadingAnswer: false,
    answerUploaded: false
  });

  // Helper function để update state
  const updateState = (updates: Partial<ExamHistoryPageState>) => {
    setState(prev => ({ ...prev, ...updates }));
  };

  // Lấy danh sách khóa học khi component được mount
  useEffect(() => {
    fetchCourses();
    fetchExamHistory();
  }, []);

  // Lấy danh sách môn học khi khóa học thay đổi
  useEffect(() => {
    if (state.selectedCourse) {
      fetchSubjects(state.selectedCourse);
    } else {
      updateState({ subjects: [] });
    }
  }, [state.selectedCourse]);

  // Lấy danh sách khóa học từ API
  const fetchCourses = async (): Promise<void> => {
    try {
      updateState({ loading: true });
      const response = await courseService.getCourses();
      updateState({ 
        courses: response.data || [],
        loading: false 
      });
    } catch (error) {
      console.error('Lỗi khi lấy danh sách khóa học:', error);
      message.error('Không thể lấy danh sách khóa học');
      updateState({ loading: false });
    }
  };

  // Lấy danh sách môn học từ API theo khóa học
  const fetchSubjects = async (khoaHocId: string): Promise<void> => {
    try {
      updateState({ loading: true });
      const response = await getSubjectsByCourse(khoaHocId);
      updateState({ 
        subjects: response,
        loading: false 
      });
    } catch (error) {
      console.error('Lỗi khi lấy danh sách môn học:', error);
      message.error('Không thể lấy danh sách môn học');
      updateState({ loading: false });
    }
  };

  // Lấy lịch sử đề thi
  const fetchExamHistory = async (): Promise<void> => {
    try {
      updateState({ loading: true });
      
      const query: ExamHistoryQuery = {};
      
      if (state.selectedCourse) {
        query.khoaHocId = state.selectedCourse;
      }
      
      if (state.selectedSubject) {
        query.monHocId = state.selectedSubject;
      }
      
      if (state.dateRange && state.dateRange.length === 2) {
        query.fromDate = state.dateRange[0].format('YYYY-MM-DD');
        query.toDate = state.dateRange[1].format('YYYY-MM-DD');
      }
      
      const response = await generateExamService.getExamHistory(query);
      console.log('📊 Exam history response:', response);
      
      updateState({ 
        exams: response.data || [],
        loading: false 
      });
    } catch (error) {
      console.error('Lỗi khi lấy lịch sử đề thi:', error);
      message.error('Không thể lấy lịch sử đề thi');
      updateState({ loading: false });
    }
  };

  // Lấy chi tiết đề thi
  const fetchExamDetail = async (deThiId: number): Promise<void> => {
    try {
      updateState({ loading: true });
      const response = await generateExamService.getExamDetail(deThiId);
      
      // Transform GeneratedExam to ExamDetail structure
      const examDetail: ExamDetail = {
        deThi: {
          DeThiID: response.DeThiID,
          TenMonHoc: response.TenMonHoc || '',
          TenKhoaHoc: response.TenKhoaHoc || '',
          NgayThi: dayjs(response.NgayThi).format('YYYY-MM-DD'),
          TongThoiGianLamBai: response.TongThoiGianLamBai,
          NguoiTao: response.NguoiTao || '',
          ThoiDiemTao: dayjs(response.ThoiDiemTao).format('YYYY-MM-DD HH:mm:ss'),
          FileDeThiWordPath: response.FileDeThiWordPath,
          FileDapAnPath: response.FileDapAnPath,
          NguoiTaoFullName: response.NguoiTao || '',
          NguoiTaoDe: response.NguoiTaoDe
        },
        cauHoi: (response.cauHoi || []).map(q => ({
          ThuTuTrongDe: q.ThuTuTrongDe,
          ThoiGianDuKienKhiTaoDe: q.ThoiGianDuKienKhiTaoDe,
          NoiDungCauHoiKhiTaoDe: q.NoiDungCauHoiKhiTaoDe
        }))
      };
      
      updateState({ 
        examDetail,
        loading: false,
        drawerVisible: true 
      });
    } catch (error) {
      console.error('Lỗi khi lấy chi tiết đề thi:', error);
      message.error('Không thể lấy chi tiết đề thi');
      updateState({ loading: false });
    }
  };

  // Tải file Word đề thi
  const downloadWordFile = async (deThiId: number): Promise<void> => {
    try {
      updateState({ loading: true });
      await generateExamService.downloadWordFile(deThiId);
      message.success('Tải file Word thành công');
      updateState({ loading: false });
    } catch (error: any) {
      console.error('Lỗi khi tải file Word:', error);
      message.error('Không thể tải file Word: ' + (error.response?.data?.error || error.message));
      updateState({ loading: false });
    }
  };

  // Tải file đáp án
  const downloadAnswerFile = async (deThiId: number): Promise<void> => {
    try {
      updateState({ loading: true });
      await generateExamService.downloadAnswerFile(deThiId);
      message.success('Tải file đáp án thành công');
      updateState({ loading: false });
    } catch (error: any) {
      console.error('Lỗi khi tải file đáp án:', error);
      message.error('Không thể tải file đáp án: ' + (error.response?.data?.error || error.message));
      updateState({ loading: false });
    }
  };

  // Upload file đáp án
  const uploadAnswerFile = async (deThiId: number): Promise<void> => {
    if (!state.answerFile) {
      message.error('Vui lòng chọn file đáp án trước');
      return;
    }

    try {
      console.log('🚀 Starting upload for exam:', deThiId);
      console.log('📁 File from state:', {
        name: state.answerFile.name,
        size: state.answerFile.size,
        type: state.answerFile.type,
        isFile: state.answerFile instanceof File
      });
      
      // Đảm bảo là File object
      if (!(state.answerFile instanceof File)) {
        throw new Error('File object không hợp lệ. Vui lòng thử chọn lại file.');
      }
      
      updateState({ uploadingAnswer: true });
      
      const result = await generateExamService.uploadAnswerFile(deThiId, state.answerFile);
      console.log('✅ Upload successful:', result);
      
      message.success('Upload đáp án thành công');
      updateState({ answerFile: null });
      
      // Cập nhật thông tin đề thi sau khi upload đáp án
      await fetchExamDetail(deThiId);
      
      // Cập nhật danh sách đề thi
      await fetchExamHistory();
      
      updateState({ 
        answerUploaded: true,
        uploadingAnswer: false 
      });
    } catch (error: any) {
      console.error('❌ Upload error details:', {
        message: error.message,
        response: error.response,
        status: error.response?.status,
        data: error.response?.data
      });
      
      const errorMessage = error.response?.data?.error || error.message || 'Lỗi không xác định';
      message.error('Không thể upload đáp án: ' + errorMessage);
      updateState({ uploadingAnswer: false });
    }
  };

  // Reset trạng thái upload sau khi đóng drawer
  const handleCloseDrawer = (): void => {
    updateState({
      drawerVisible: false,
      answerFile: null,
      answerUploaded: false
    });
  };

  // Xem chi tiết đề thi
  const viewExamDetail = (record: ExamHistory): void => {
    updateState({ selectedExam: record });
    fetchExamDetail(record.DeThiID);
  };

  // Xử lý tìm kiếm
  const handleSearch = (): void => {
    fetchExamHistory();
  };

  // Xử lý reset bộ lọc
  const handleReset = (): void => {
    updateState({
      selectedCourse: null,
      selectedSubject: null,
      dateRange: null
    });
    
    // Fetch lại dữ liệu không có filter
    setTimeout(() => {
      fetchExamHistory();
    }, 100);
  };

  // Handler cho thay đổi course
  const handleCourseChange = (value: string): void => {
    updateState({ selectedCourse: value });
  };

  // Handler cho thay đổi subject
  const handleSubjectChange = (value: string): void => {
    updateState({ selectedSubject: value });
  };

  // Handler cho thay đổi date range
  const handleDateRangeChange = (dates: any, dateStrings: [string, string]): void => {
    if (dates && dates.length === 2) {
      updateState({ dateRange: [dates[0], dates[1]] });
    } else {
      updateState({ dateRange: null });
    }
  };

  // Cấu hình các cột của bảng
  const columns = [
    {
      title: 'STT',
      key: 'index',
      width: 60,
      render: (text: any, record: ExamHistory, index: number) => index + 1,
    },
    {
      title: 'Môn học',
      dataIndex: 'TenMonHoc',
      key: 'TenMonHoc',
      width: 200,
    },
    {
      title: 'Khóa học',
      dataIndex: 'TenKhoaHoc',
      key: 'TenKhoaHoc',
      width: 150,
    },
    {
      title: 'Ngày thi',
      dataIndex: 'NgayThi',
      key: 'NgayThi',
      width: 120,
      render: (text: string) => dayjs(text).format('DD/MM/YYYY'),
    },
    {
      title: 'Thời gian làm bài',
      dataIndex: 'TongThoiGianLamBai',
      key: 'TongThoiGianLamBai',
      width: 150,
      render: (text: number) => `${text} phút`,
    },
    {
      title: 'Người tạo',
      dataIndex: 'NguoiTao',
      key: 'NguoiTao',
      width: 150,
    },
    {
      title: 'Thời gian tạo',
      dataIndex: 'ThoiDiemTao',
      key: 'ThoiDiemTao',
      width: 170,
      render: (text: string) => dayjs(text).format('DD/MM/YYYY HH:mm'),
    },
    {
      title: 'Hành động',
      key: 'action',
      width: 180,
      render: (text: any, record: ExamHistory) => (
        <Space size="small">
          <Tooltip title="Xem chi tiết">
            <Button
              type="primary"
              ghost
              icon={<EyeOutlined />}
              onClick={() => viewExamDetail(record)}
            />
          </Tooltip>
          <Tooltip title="Tải file Word">
            <Button
              type="primary"
              ghost
              icon={<FileWordOutlined />}
              onClick={() => downloadWordFile(record.DeThiID)}
              disabled={!record.FileDeThiWordPath}
            />
          </Tooltip>
          <Tooltip title="Tải đáp án">
            <Button
              type="primary"
              ghost
              icon={<FileOutlined />}
              onClick={() => downloadAnswerFile(record.DeThiID)}
              disabled={!record.FileDapAnPath}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div className="exam-history-page">
      <Card title="Lịch Sử Đề Thi Đã Tạo" style={{ border: 'none' }}>
        <div className="filters" style={{ marginBottom: 16 }}>
          <Space wrap>
            <Select
              placeholder="Chọn khóa học"
              style={{ width: 200 }}
              value={state.selectedCourse}
              onChange={handleCourseChange}
              allowClear
            >
              {state.courses.map(course => (
                <Option key={course.KhoaHocID} value={course.KhoaHocID}>
                  {course.TenKhoaHoc}
                </Option>
              ))}
            </Select>
            
            <Select
              placeholder="Chọn môn học"
              style={{ width: 200 }}
              value={state.selectedSubject}
              onChange={handleSubjectChange}
              disabled={!state.selectedCourse}
              allowClear
            >
              {state.subjects.map(subject => (
                <Option key={subject.MonHocID} value={subject.MonHocID}>
                  {subject.TenMonHoc}
                </Option>
              ))}
            </Select>
            
            <RangePicker
              placeholder={['Từ ngày', 'Đến ngày']}
              format="DD/MM/YYYY"
              value={state.dateRange}
              onChange={handleDateRangeChange}
            />
            
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={handleSearch}
            >
              Tìm kiếm
            </Button>
            
            <Button onClick={handleReset}>
              Reset
            </Button>
          </Space>
        </div>
        
        <Table
          columns={columns}
          dataSource={state.exams}
          rowKey="DeThiID"
          loading={state.loading}
          pagination={{ pageSize: 10 }}
          scroll={{ x: 1100 }}
        />
      </Card>
      
      <Drawer
        title={`Chi tiết đề thi ${state.selectedExam?.TenMonHoc || ''}`}
        placement="right"
        onClose={handleCloseDrawer}
        open={state.drawerVisible}
        width={600}
      >
        {state.examDetail && (
          <>
            <Descriptions bordered column={1}>
              <Descriptions.Item label="Môn học">{state.examDetail.deThi.TenMonHoc}</Descriptions.Item>
              <Descriptions.Item label="Khóa học">{state.examDetail.deThi.TenKhoaHoc}</Descriptions.Item>
              <Descriptions.Item label="Ngày thi">{dayjs(state.examDetail.deThi.NgayThi).format('DD/MM/YYYY')}</Descriptions.Item>
              <Descriptions.Item label="Thời gian làm bài">{state.examDetail.deThi.TongThoiGianLamBai} phút</Descriptions.Item>
              <Descriptions.Item label="Người tạo">{state.examDetail.deThi.NguoiTaoFullName || state.examDetail.deThi.NguoiTaoDe}</Descriptions.Item>
              <Descriptions.Item label="Thời gian tạo">{dayjs(state.examDetail.deThi.ThoiDiemTao).format('DD/MM/YYYY HH:mm')}</Descriptions.Item>
              <Descriptions.Item label="File đề thi">
                {state.examDetail.deThi.FileDeThiWordPath ? (
                  <Button
                    type="link"
                    icon={<FileWordOutlined />}
                    onClick={() => downloadWordFile(state.examDetail!.deThi.DeThiID)}
                  >
                    Tải xuống file đề thi
                  </Button>
                ) : (
                  <span>Chưa có file</span>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="File đáp án">
                {state.examDetail.deThi.FileDapAnPath ? (
                  <Button
                    type="link"
                    icon={<FileOutlined />}
                    onClick={() => downloadAnswerFile(state.examDetail!.deThi.DeThiID)}
                  >
                    Tải xuống file đáp án
                  </Button>
                ) : (
                  <div>
                    <span>Chưa có file đáp án</span>
                    <div style={{ marginTop: 8 }}>
                      <Upload
                        maxCount={1}
                        beforeUpload={(file) => {
                          console.log('🔄 Upload beforeUpload triggered with file:', {
                            name: file.name,
                            size: file.size,
                            type: file.type,
                            isFile: file instanceof File,
                            constructor: file.constructor.name
                          });
                          
                          // QUAN TRỌNG: Lưu File object trực tiếp như hệ thống cũ
                          updateState({ answerFile: file });
                          console.log('✅ File saved to state:', file);
                          return false; // Ngăn upload tự động
                        }}
                        onRemove={() => {
                          console.log('🗑️ File removed from upload');
                          updateState({ answerFile: null });
                        }}
                        accept=".doc,.docx,.pdf,.xls,.xlsx,.txt"
                        fileList={state.answerFile ? [{
                          uid: 'answer-file',
                          name: state.answerFile.name,
                          status: 'done' as const,
                          size: state.answerFile.size,
                          type: state.answerFile.type,
                          originFileObj: state.answerFile as any
                        } as UploadFile] : []}
                      >
                        <Button icon={<UploadOutlined />} size="small">Chọn file đáp án</Button>
                      </Upload>
                      
                      <Button
                        type="primary"
                        onClick={() => uploadAnswerFile(state.examDetail!.deThi.DeThiID)}
                        disabled={!state.answerFile}
                        loading={state.uploadingAnswer}
                        size="small"
                        style={{ marginTop: 8 }}
                      >
                        Tải lên đáp án
                      </Button>
                      
                      <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {/* Test connection button */}
                        <Button
                          type="primary"
                          onClick={async () => {
                            try {
                              console.log('🔗 Testing backend connection...');
                              const result = await testBackend();
                              message.success('Backend OK! Status: ' + result.data?.message || 'Connected');
                            } catch (error: any) {
                              console.error('❌ Connection test failed:', error);
                              let errorMsg = 'Backend connection failed';
                              if (error.message.includes('ERR_NETWORK') || error.message.includes('fetch')) {
                                errorMsg += ': Server không chạy hoặc không thể kết nối. Hãy chạy start-backend.bat';
                              } else if (error.message.includes('404')) {
                                errorMsg += ': API endpoint không tồn tại';
                              } else if (error.message.includes('401')) {
                                errorMsg += ': Token hết hạn hoặc không hợp lệ';
                              } else {
                                errorMsg += `: ${error.message}`;
                              }
                              message.error(errorMsg);
                            }
                          }}
                          size="small"
                        >
                          Test Backend
                        </Button>

                        {/* Debug file structure button */}
                        <Button
                          type="dashed"
                          onClick={() => {
                            console.log('🔍 === FILE STRUCTURE DEBUG ===');
                            console.log('🔍 Raw answerFile:', state.answerFile);
                            
                            if (state.answerFile) {
                              console.log('🔍 Direct properties:', {
                                name: state.answerFile.name,
                                size: state.answerFile.size,
                                type: state.answerFile.type,
                                isFile: state.answerFile instanceof File,
                                constructor: state.answerFile.constructor.name
                              });
                              
                              // Kiểm tra originFileObj
                              const originFile = (state.answerFile as any).originFileObj;
                              if (originFile) {
                                console.log('🔍 originFileObj properties:', {
                                  name: originFile.name,
                                  size: originFile.size,
                                  type: originFile.type,
                                  isFile: originFile instanceof File,
                                  constructor: originFile.constructor.name
                                });
                              }
                              
                              // Test FormData
                              try {
                                const testFormData = new FormData();
                                const fileToTest = originFile || state.answerFile;
                                testFormData.append('test', fileToTest);
                                console.log('🔍 FormData test SUCCESS');
                              } catch (e) {
                                console.error('🔍 FormData test FAILED:', e);
                              }
                            }
                            console.log('🔍 === END DEBUG ===');
                          }}
                          disabled={!state.answerFile}
                          size="small"
                        >
                          Debug File
                        </Button>

                        {/* Test upload button */}
                        <Button
                          type="default"
                          onClick={async () => {
                            if (state.answerFile) {
                              try {
                                console.log('🧪 Starting test upload...');
                                console.log('🧪 File from state:', {
                                  name: state.answerFile.name,
                                  size: state.answerFile.size,
                                  type: state.answerFile.type,
                                  isFile: state.answerFile instanceof File
                                });
                                
                                if (!(state.answerFile instanceof File)) {
                                  throw new Error('File object không hợp lệ cho test upload');
                                }
                                
                                const result = await generateExamService.testUpload(state.answerFile);
                                console.log('🧪 Test upload success:', result);
                                message.success('Test upload thành công!');
                              } catch (error: any) {
                                console.error('🧪 Test upload failed:', error);
                                let errorMsg = 'Test upload thất bại';
                                if (error.code === 'ERR_NETWORK') {
                                  errorMsg += ': Không thể kết nối đến server';
                                } else if (error.response?.status) {
                                  errorMsg += `: ${error.response.status} - ${error.response.data?.error || error.response.statusText}`;
                                } else {
                                  errorMsg += `: ${error.message}`;
                                }
                                message.error(errorMsg);
                              }
                            } else {
                              message.warning('Vui lòng chọn file trước khi test');
                            }
                          }}
                          disabled={!state.answerFile}
                          size="small"
                        >
                          Test Upload
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </Descriptions.Item>
            </Descriptions>
            
            <Divider orientation="left">Câu hỏi trong đề thi</Divider>
            
            <List
              dataSource={state.examDetail.cauHoi}
              renderItem={(item) => (
                <List.Item>
                  <List.Item.Meta
                    title={<span>Câu {item.ThuTuTrongDe}: <Tag color="blue">{item.ThoiGianDuKienKhiTaoDe} phút</Tag></span>}
                    description={item.NoiDungCauHoiKhiTaoDe}
                  />
                </List.Item>
              )}
              bordered
            />
          </>
        )}
      </Drawer>
    </div>
  );
};

export default ExamHistoryPage;
